import api from "@/services/api"
import { queryClient } from "@/queryClient"
import type {
  TRANSACTION_METHOD,
  TRANSACTION_TYPE,
  TransactionSignatureStatus,
  TransactionStatus,
  TransactionType,
} from "@/services/sharedTypes/transactions"
import type { CardTransactionData } from "@/androidPlugins/IPayment"
import type { RecursivelyConvertDatesToStrings } from "@/utils/typeUtilities"
import type {
  PaymentDetailCard,
  PaymentDetailCash,
  PaymentDetailSplit,
} from "@/services/sharedTypes/invoices"
import {
  BASE_ORDERS_QUERY_KEY,
  BASE_SUMMARY_REPORT_QUERY_KEY,
  BASE_TRANSACTION_REPORT_QUERY_KEY,
} from "./queryKeys"

type CashTransactionData = {
  cashTendered: number
  amountGratuity: number
}

type CashTransactionIn = {
  source: "pos_cash"
  data: CashTransactionData
  forceInvoicePaid?: boolean
}

type CardTransactionIn = {
  source: "pos_card"
  data: CardTransactionData
}

type SplitCardTransactionIn = {
  source: "pos_card_split"
  data: CardTransactionData
  skipInvoicePaidCheck?: boolean
}

type SplitCashTransactionIn = {
  source: "pos_cash_split"
  data: {
    cashTendered: number
    changeGiven: number
    amountDue?: number
  }
  skipInvoicePaidCheck?: boolean
}

export type CreateTransactionBody =
  | CashTransactionIn
  | CardTransactionIn
  | SplitCardTransactionIn
  | SplitCashTransactionIn

export type CreateTransactionResponseInvoice = {
  id: string
  isPaid: boolean
  paidBy:
    | PaymentDetailCard
    | PaymentDetailCash
    | PaymentDetailSplit
    | null
  amountBalancedGross: number
  amountBalancedGratuity: number
  amountBalancedCashback: number
  amountBalancedTotal: number
}

type CreateTransactionResponseRefund = {
  id: string
  type: TransactionType
  createdAt: Date
  invoiceId: string
  parentTransactionId: string | null
  amountTransactedGross: number
  amountTransactedGratuity: number
  amountTransactedTotal: number
  currency: string
  transactionMethod: string
  uti: string | null
  status: TransactionStatus
  deviceSerialNumber: string | null
}

type BaseCreateTransactionResponse = {
  id: string
  invoiceId: string
  createdAt: Date
  parentTransactionId: string | null
  amountTransactedGross: number
  amountTransactedGratuity: number
  amountTransactedTotal: number
  currency: string
  deviceSerialNumber: string | null
  status: TransactionStatus
  invoice: CreateTransactionResponseInvoice
  type: typeof TRANSACTION_TYPE.PAYMENT
  amountCashback: number
  refunds: Array<CreateTransactionResponseRefund>
}

type CreateTransactionCardResponse =
  BaseCreateTransactionResponse & {
    transactionMethod: (typeof TRANSACTION_METHOD)["POS_CARD"]
    cardScheme: string | null
    cardNumberLast4: string
    acquirerResponseCode: string | null
    amountCashback: number
    signatureStatus: TransactionSignatureStatus | null
    retrievalReferenceNumber: string | null
    uti: string | null
    isDcc: boolean
  }

type CreateTransactionCashResponse =
  BaseCreateTransactionResponse & {
    transactionMethod: (typeof TRANSACTION_METHOD)["POS_CASH"]
    cashTendered: number
    changeGiven: number
  }

type CreateTransactionUnknownResponse =
  BaseCreateTransactionResponse & {
    transactionMethod: null
  }

export type CreateTransactionResponse =
  | CreateTransactionCardResponse
  | CreateTransactionCashResponse
  | CreateTransactionUnknownResponse

export const createTransaction = async (
  invoiceId: string,
  transIn: CreateTransactionBody
): Promise<CreateTransactionResponse> => {
  const { body } = await api.post<
    RecursivelyConvertDatesToStrings<CreateTransactionResponse>
  >(`/invoices/${invoiceId}/transactions/`, {
    body: transIn,
  })

  queryClient.invalidateQueries({
    queryKey: [BASE_ORDERS_QUERY_KEY],
  })
  queryClient.invalidateQueries({
    queryKey: [BASE_SUMMARY_REPORT_QUERY_KEY],
  })
  queryClient.invalidateQueries({
    queryKey: [BASE_TRANSACTION_REPORT_QUERY_KEY],
  })

  return {
    ...body,
    createdAt: DateFns.parseISO(body.createdAt),
    refunds: body.refunds.map((refund) => ({
      ...refund,
      createdAt: DateFns.parseISO(refund.createdAt),
    })),
  }
}
